import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface ConfirmEmailDialogData {
  defaultTo: string[];
  defaultCc: string[];
  year: number;
  month: number;
}

export interface ConfirmEmailDialogResult {
  toRecipients: string[];
  ccRecipients: string[];
  additionalEmails: string[];
}

type RecipientField = 'to' | 'cc';

@Component({
  selector: 'app-confirm-email-modal',
  templateUrl: './confirm-email-modal.component.html',
  styleUrl: './confirm-email-modal.component.scss',
})
export class ConfirmEmailModalComponent {
  toChips: string[];
  ccChips: string[];
  additionalEmails: string[] = [];

  toInputValue = '';
  ccInputValue = '';
  errorMessage: string | null = null;

  private readonly emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  constructor(
    private readonly dialogRef: MatDialogRef<ConfirmEmailModalComponent, ConfirmEmailDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: ConfirmEmailDialogData,
  ) {
    this.toChips = [...(data.defaultTo ?? [])];
    this.ccChips = [...(data.defaultCc ?? [])];
  }

  removeToChip(email: string): void {
    this.toChips = this.toChips.filter((e) => e !== email);
    this.additionalEmails = this.additionalEmails.filter((e) => e !== email);
  }

  removeCcChip(email: string): void {
    this.ccChips = this.ccChips.filter((e) => e !== email);
  }

  onInputKey(field: RecipientField, event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ',' || event.key === ';') {
      event.preventDefault();
      this.commit(field);
    }
  }

  onBlur(field: RecipientField): void {
    this.commit(field);
  }

  commit(field: RecipientField): void {
    const raw = (field === 'to' ? this.toInputValue : this.ccInputValue)
      .trim()
      .replace(/[,;]+$/, '');
    if (!raw) {
      this.clearInput(field);
      this.errorMessage = null;
      return;
    }
    if (!this.emailPattern.test(raw)) {
      this.errorMessage = `"${raw}" is not a valid email address`;
      return;
    }
    if (this.allEmails().some((e) => e.toLowerCase() === raw.toLowerCase())) {
      this.errorMessage = `"${raw}" is already in the list`;
      this.clearInput(field);
      return;
    }
    if (field === 'to') {
      this.toChips = [...this.toChips, raw];
      this.additionalEmails = [...this.additionalEmails, raw];
    } else {
      this.ccChips = [...this.ccChips, raw];
    }
    this.clearInput(field);
    this.errorMessage = null;
  }

  cancel(): void {
    this.dialogRef.close();
  }

  send(): void {
    this.commit('to');
    this.commit('cc');
    if (this.errorMessage) {
      return;
    }
    if (this.toChips.length === 0) {
      this.errorMessage = 'At least one "To" recipient is required';
      return;
    }
    this.dialogRef.close({
      toRecipients: [...this.toChips],
      ccRecipients: [...this.ccChips],
      additionalEmails: [...this.additionalEmails],
    });
  }

  private clearInput(field: RecipientField): void {
    if (field === 'to') {
      this.toInputValue = '';
    } else {
      this.ccInputValue = '';
    }
  }

  private allEmails(): string[] {
    return [...this.toChips, ...this.ccChips];
  }
}
